import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useProjectFilter } from "../../../context/ProjectFilterContext";
import NoSelected from "@components/NoSelected";
import Spinner from "@components/Spinner/Spinner";
import AddButton from "@components/Button/AddButton";
import { Col, Container, Dropdown, Offcanvas, Row } from "react-bootstrap";
import ServerError from "@components/Error/ServerError";
import Breadcrumb from "@components/Breadcrumb/Breadcrumb";
import { usePaginatedTrees } from "@hooks/usePaginatedData";
import { useBackgroundTrees } from "@hooks/useBackgroundData";
import { useUrlListState } from "@hooks/useUrlListState";
import { ImportTreeButton } from "./Button/ImportTreeButton";
import TreeList from "./TreeList";
import TreeImageForm from "./Form/AddForm/TreeImageForm";
import TreeDetail from "./TreeDetail";
import TableActions from "@components/TableActions";
import { useNavigate } from "react-router-dom";
import useWindowDimensions from "@hooks/useWindowDimension";
import useMatchedElementHeight from "@hooks/useMatchedElementHeight";
import { hasPermission } from "@helper/permissions";
import { FileExportIcon, AngleDownIcon } from "@components/icons";
import DownloadExcelButton from "@modules/Projects/pages/ProjectDetail/Button/DownloadExcelButton";
import TreePDFExportPage from "./pdf/PDFExport";

export default function TreePage() {
  const { t } = useTranslation();
  const [formValue, setFormValue] = useState();
  const [openImageForm, setOpenImageForm] = useState(false);
  const { mobile } = useSelector((state) => state.mobile);
  const authUser = useSelector((state) => state.auth?.user);
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const tid = query.get("selectedTree");
  const fromDashboard = query.get("from") === "dashboard";
  const { selectedProjectId } = useProjectFilter();
  const { trees: allTrees } = useBackgroundTrees();
  const urlListConfig = React.useMemo(
    () => ({
      defaults: {
        page: 1,
        pageSize: 20,
        search: "",
        ordering: "",
        status: [],
        area: [],
      },
      intKeys: ["page", "pageSize"],
      arrayKeys: ["status", "area"],
    }),
    []
  );
  const { state: listState, setValue: setListValue } = useUrlListState(urlListConfig);
  const navigate = useNavigate();
  const { width } = useWindowDimensions();
  const useRightDrawer = width < 992;
  const listColumnRef = React.useRef(null);
  const detailHeight = useMatchedElementHeight(
    listColumnRef,
    ".table-wrapper",
    !mobile && tid && !useRightDrawer
  );
  const canCreateTree =
    Number(authUser?.account_type) !== 2 && hasPermission(authUser, "edit_tree");
  const { page, pageSize, search, ordering, status, area } = listState;

  // Reset to page 1 when project filter changes
  React.useEffect(() => {
    setListValue("page", 1);
  }, [selectedProjectId]);

  const {
    data: treeResponse,
    isLoading,
    isError,
  } = usePaginatedTrees({
    page,
    pageSize,
    search,
    projectId: selectedProjectId,
    ordering,
    status,
    area,
  });

  const treeData = treeResponse?.results || [];
  const totalCount = treeResponse?.count || 0;
  const hasNext = !!treeResponse?.next;
  const hasPrevious = !!treeResponse?.previous;
  const areaOptions = React.useMemo(() => {
    const sourceTrees = selectedProjectId
      ? allTrees.filter((tree) => String(tree.project_id) === String(selectedProjectId))
      : allTrees;
    const byId = new Map();
    sourceTrees.forEach((tree) => {
      const id = String(tree.area_id || tree?.area?.id || "");
      const name = tree.area_name || tree?.area?.name || "";
      if (!id || !name || byId.has(id)) return;
      byId.set(id, { id, name });
    });
    return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [allTrees, selectedProjectId]);

  // Filtered trees for export (apply same filters as server-side pagination)
  const filteredTreesForExport = React.useMemo(() => {
    let filtered = allTrees;

    // Filter by project if selected
    if (selectedProjectId) {
      filtered = filtered.filter((tree) => String(tree.project_id) === String(selectedProjectId));
    }

    // Filter by search
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(
        (tree) =>
          tree.name?.toLowerCase().includes(searchLower) ||
          tree.area_name?.toLowerCase().includes(searchLower) ||
          tree.species?.name?.toLowerCase().includes(searchLower)
      );
    }

    // Filter by status
    if (status?.length > 0) {
      filtered = filtered.filter((tree) => status.includes(tree.health_state));
    }

    // Filter by area
    if (area?.length > 0) {
      filtered = filtered.filter((tree) => area.includes(String(tree.area_id || tree.area?.id)));
    }

    return filtered;
  }, [allTrees, selectedProjectId, search, status, area]);

  // Export title with current date
  const exportTitle = React.useMemo(() => {
    const d = new Date();
    const isoDate = d.toISOString().split("T")[0];
    return `${t("tree_cadastre", "Baumkataster")}_${isoDate}`;
  }, [t]);

  // Export dropdown component
  const ExportDropdown = (
    <Dropdown align="end" key="export">
      <Dropdown.Toggle variant="outline-secondary" size="sm">
        <span className="d-inline-flex align-items-center gap-2">
          <FileExportIcon />
          <span>{t("export", "Export")}</span>
          <AngleDownIcon />
        </span>
      </Dropdown.Toggle>
      <Dropdown.Menu>
        <DownloadExcelButton data={filteredTreesForExport} title={exportTitle} renderMode="menu" />
        <TreePDFExportPage trees={filteredTreesForExport} title={exportTitle} renderMode="menu" />
      </Dropdown.Menu>
    </Dropdown>
  );

  if (isLoading && !treeResponse) return <Spinner height={"80vh"} />;
  if (isError) return <ServerError />;

  const closeTreeDrawer = () => {
    const params = new URLSearchParams(location.search);
    params.delete("selectedTree");
    navigate(`/trees${params.toString() ? `?${params.toString()}` : ""}`);
  };

  return (
    <>
      {!mobile && fromDashboard && <Breadcrumb backLink="/dashboard" />}
      <Container className={!mobile ? "entity-list-page" : undefined} data-tour="tree-page">
        <Row className="g-3">
          <Col
            ref={listColumnRef}
            lg={useRightDrawer ? 12 : tid ? 6 : 12}
            data-tour="tree-list"
            className="entity-master-column"
          >
            <TreeList
              trees={treeData}
              setOpenImageForm={setOpenImageForm}
              setFormValue={setFormValue}
              onEdit={(tree) => navigate(`/map?selectedTree=${tree.id}&editTree=1`)}
              page={page}
              setPage={(value) => setListValue("page", value)}
              pageSize={pageSize}
              setPageSize={(value) => setListValue("pageSize", value)}
              search={search}
              setSearch={(value) => setListValue("search", value)}
              ordering={ordering}
              setOrdering={(value) => setListValue("ordering", value)}
              selectedStatus={status}
              setSelectedStatus={(value) => setListValue("status", value)}
              selectedAreaIds={area}
              setSelectedAreaIds={(value) => setListValue("area", value)}
              totalCount={totalCount}
              hasNext={hasNext}
              hasPrevious={hasPrevious}
              isLoading={isLoading}
              areaOptions={areaOptions}
              buttons={({ isNarrow }) =>
                canCreateTree ? (
                  <TableActions
                    onAdd={() => navigate("/map?addTree=1&origin=trees")}
                    addLabelKey={"button.add_tree"}
                    addTestId="add-tree-button"
                    collapseSecondary={isNarrow}
                    secondary={[<ImportTreeButton key="import" />, ExportDropdown]}
                  />
                ) : (
                  ExportDropdown
                )
              }
            />
          </Col>
          {tid && !useRightDrawer && (
            <Col lg={6} className="tree-detail-column entity-detail-column">
              {!tid ? (
                <NoSelected text={"project_details.no_tree_selected"} />
              ) : (
                <div
                  className="entity-detail-height-shell"
                  style={detailHeight ? { height: `${detailHeight}px` } : undefined}
                >
                  <TreeDetail trees={treeData} />
                </div>
              )}
            </Col>
          )}
        </Row>
      </Container>
      {useRightDrawer && (
        <Offcanvas
          show={Boolean(tid)}
          onHide={closeTreeDrawer}
          placement="end"
          backdrop={false}
          scroll={false}
          style={{
            width: "min(92vw, 560px)",
            top: "4rem",
            height: "calc(100dvh - 4rem)",
            zIndex: 1020,
          }}
        >
          <Offcanvas.Body className="p-0">
            <TreeDetail trees={treeData} />
          </Offcanvas.Body>
        </Offcanvas>
      )}
      {openImageForm && (
        <TreeImageForm show={openImageForm} formValue={formValue} setOpenForm={setOpenImageForm} />
      )}
    </>
  );
}
